import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import { SlidersHorizontal } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, TYPOGRAPHY } from '../../theme';
import { TextTabs } from '../common';

export type FilterOption = 'all' | 'mine';

// The feed is news-only now (see postsStore's FEED_CATEGORY) — the old
// Progress/Opportunities/News category chips no longer apply, since every
// post in the list already is news.
export const BASE_FILTERS: { key: FilterOption; label: string }[] = [
  { key: 'all', label: 'All news' },
];

export function buildFilters(showMine: boolean): { key: FilterOption; label: string }[] {
  if (!showMine) return BASE_FILTERS;
  return [BASE_FILTERS[0], { key: 'mine', label: 'My news' }];
}

/**
 * Permanent feed filters: All news / My news. Plaud grammar: underlined
 * text tabs, not chips.
 */
export function FilterChips({
  filter,
  setFilter,
  filters,
  style,
}: {
  filter: FilterOption;
  setFilter: (f: FilterOption) => void;
  filters: { key: FilterOption; label: string }[];
  style?: ViewStyle;
}) {
  return (
    <TextTabs
      tabs={filters}
      activeKey={filter}
      onChange={(key) => setFilter(key as FilterOption)}
      testID="feed-filter-tabs"
      style={style}
    />
  );
}

export function FilterDropdown({
  filter,
  setFilter,
  isOpen,
  onToggle,
  colors,
  filters,
}: {
  filter: FilterOption;
  setFilter: (f: FilterOption) => void;
  isOpen: boolean;
  onToggle: () => void;
  colors: ReturnType<typeof useTheme>['colors'];
  filters: { key: FilterOption; label: string }[];
}) {
  const handleSelect = (key: FilterOption) => {
    setFilter(key);
    onToggle();
  };

  return (
    <View>
      <TouchableOpacity
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityLabel={`Filter: ${filters.find((f) => f.key === filter)?.label ?? 'All'}`}
        hitSlop={8}
        style={styles.filterButton}>
        <SlidersHorizontal size={20} color={colors.text} />
        {filter !== 'all' && (
          <View style={[styles.filterDot, { backgroundColor: colors.primary }]} />
        )}
      </TouchableOpacity>
      {isOpen && (
        <>
          <TouchableOpacity style={styles.dropdownBackdrop} activeOpacity={1} onPress={onToggle} />
          <View
            style={[
              styles.dropdown,
              { backgroundColor: colors.surface, borderColor: colors.border },
              ELEVATION.md,
            ]}>
            {filters.map((f) => {
              const isActive = filter === f.key;
              return (
                <TouchableOpacity
                  key={f.key}
                  onPress={() => handleSelect(f.key)}
                  style={[
                    styles.dropdownItem,
                    isActive && { backgroundColor: colors.backgroundSecondary },
                  ]}
                  activeOpacity={0.7}>
                  <Text
                    style={[
                      styles.dropdownLabel,
                      { color: isActive ? colors.text : colors.textSecondary },
                      isActive && TYPOGRAPHY.chip,
                    ]}>
                    {f.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  filterButton: {
    position: 'relative',
    padding: SPACING.xs,
  },
  filterDot: {
    position: 'absolute',
    top: 4,
    right: 2,
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  dropdownBackdrop: {
    position: 'fixed' as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 199,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: SPACING.xs,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    minWidth: 140,
    overflow: 'hidden',
    zIndex: 200,
  },
  dropdownItem: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd,
  },
  dropdownLabel: {
    ...TYPOGRAPHY.label,
  },
});
